/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import React, { useEffect } from 'react';

import { get, values, startCase, camelCase } from 'lodash';
import Element from '../../../../components/element';
import Button from '../../../../components/Button';
import Flex from '../../../../components/flex';
import Text from '../../../../components/text';
import NoData from '../../NoData';
import useStyles from './style';
import { create, updateData } from '../../Chart/DistributionChart/chart';

const Cognition = ({ healthReportData }) => {
  const classes = useStyles();
  const ordinal_suffix_of = i => {
    const j = i % 10;
    const k = i % 100;
    if (j == 1 && k != 11) {
      return i + 'st';
    }
    if (j == 2 && k != 12) {
      return i + 'nd';
    }
    if (j == 3 && k != 13) {
      return i + 'rd';
    }
    return i + 'th';
  };
  useEffect(() => {
    const attention = values(healthReportData.cognition).slice(1, 3);
    const executive_function = values(
      healthReportData.cognition.executive_function
    );
    const memory = values(healthReportData.cognition.memory);
    const newCognitionList = attention.concat(executive_function, memory);
    newCognitionList.map(each => {
      const data = {
        ...each,
        section: 'cognition',
        chartVal: isNaN(each.percentile)
          ? each.percentile.replace(/[^\d.-]/g, '')
          : each.percentile
      };
      const settings = {
        el: each.test_name.replace(/ /g, '_'),
        margin: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        }
      };
      create(settings);
      updateData(data);
    });
  }, []);
  return (
    <Element>
      {get(healthReportData, 'cognition.combined_results.value') === null ||
      get(healthReportData, 'cognition.combined_results.value') === 0 ? (
        <NoData />
      ) : (
        <Element>
          <Flex marginTop="50px">
            <Element width="30%">
              <Element marginRight="65px">
                <Text family="GTAM" size="14px" color="#222222">
                  Cognition is the mental action or process of acquiring
                  knowledge and understanding through thought, experiences, and
                  the senses.
                </Text>
              </Element>
              <Element marginRight="65px" marginTop="32px">
                <Text family="GTAM" size="14px" color="#222222">
                  Thorne Lab assesses four cognitive domains:
                </Text>
              </Element>
            </Element>
            <Element className={classes.testCard}>
              <Flex padding="10px 20px">
                <Element width="37%">
                  <Text family="GTAM" size="21px" color="#222222">
                    Combined Test Results:
                  </Text>
                  <Text
                    family="GTAM"
                    size="33px"
                    color="#222222"
                    marginTop="20px"
                  >
                    {Number(
                      get(healthReportData, 'cognition.combined_results.value')
                    ).toFixed(2)}
                    *
                  </Text>
                  <Text family="GTAR" size="14px" color="#959595">
                    Standard Score Range 0-200
                  </Text>
                </Element>
                <Element marginRight="35px">
                  <Text
                    family="GTAR"
                    size="16px"
                    color="#222222"
                    marginTop="15px"
                  >
                    {ordinal_suffix_of(
                      Number(
                        get(
                          healthReportData,
                          'cognition.combined_results.percentile'
                        )
                      ).toFixed(0)
                    )}
                    * Population Percentile,
                  </Text>
                  <Text
                    family="GTAR"
                    size="16px"
                    color="#222222"
                    marginTop="15px"
                    whiteSpace="nowrap"
                  >
                    Presence of cognitive impairment:
                  </Text>
                  <Text
                    family="GTAR"
                    size="16px"
                    color="#222222"
                    marginTop="22px"
                  >
                    Clinical correlation warranted
                  </Text>
                  <Text
                    family="GTAR"
                    size="16px"
                    color="#222222"
                    marginTop="20px"
                  >
                    Malingering Test:
                  </Text>
                </Element>
                <Element textAlign="right">
                  <Text
                    family="GTAR"
                    size="16px"
                    color="#80e0a7"
                    marginTop="15px"
                  >
                    {startCase(
                      get(healthReportData, 'cognition.combined_results.rank')
                    )}
                  </Text>
                  <Text
                    family="GTAR"
                    size="16px"
                    color="#80e0a7"
                    marginTop="15px"
                    whiteSpace="nowrap"
                  >
                    {startCase(
                      get(
                        healthReportData,
                        'cognition.combined_results.interpretation'
                      )
                    )}
                  </Text>
                  <Text
                    family="GTAR"
                    size="16px"
                    color="#80e0a7"
                    marginTop="68px"
                  >
                    {get(
                      healthReportData,
                      'cognition.combined_results.malingering'
                    )
                      ? 'Fail'
                      : 'Pass'}
                  </Text>
                </Element>
              </Flex>
            </Element>
          </Flex>
          <Element marginTop="40px">
            <Text family="GTAM" size="21px" color="#222222">
              Attention
            </Text>
            <Text
              family="GTAM"
              size="14px"
              color="#222222"
              borderBottom="2px solid #959595"
              paddingBottom="10px"
            >
              Attention represents capacity for new learning and information
              processing in novel situations, and allows us to plan, preview,
              monitor, and regulate our thoughts and actions.
            </Text>
          </Element>
          <Element>
            <Text family="GTAR" size="14px" color="#959595">
              Trails A: The Trails A test had two parts to the test. The first
              part measured your attention, and you were asked to tap numbers in
              sequential order.
            </Text>
          </Element>
          <Flex align="center" marginTop="35px">
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {get(
                  healthReportData,
                  'cognition.attention.value'
                )
                  ? get(
                      healthReportData,
                      'cognition.attention.value'
                    )
                  : '*'}
                  /200
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Standard Score
              </Text>
            </Element>
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {isNaN(get(healthReportData, 'cognition.attention.percentile'))
                  ? get(
                      healthReportData,
                      'cognition.attention.percentile'
                    ).replace(/[^\d.-]/g, '')
                  : get(healthReportData, 'cognition.attention.percentile')}
                %
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Percentile
              </Text>
            </Element>
            <div
              id={get(
                healthReportData,
                'cognition.attention.test_name'
              ).replace(/ /g, '_')}
              style={{ width: '25.6%' }}
            ></div>
            <Flex marginLeft="90px">
              <Text family="GTAR" size="16px" color="#222222">
                Impression:
                <strong>
                  {' '}
                  {startCase(
                    get(healthReportData, 'cognition.attention.interpretation')
                  )}
                </strong>
              </Text>
            </Flex>
          </Flex>

          <Element marginTop="30px">
            <Text family="GTAM" size="21px" color="#222222">
              Mental Flexibility
            </Text>
            <Text
              family="GTAM"
              size="14px"
              color="#222222"
              borderBottom="2px solid #959595"
              paddingBottom="10px"
            >
              Cognitive flexibility is the ability to switch between thinking
              about two different concepts, and to think about multiple concepts
              simultaneously.
            </Text>
          </Element>
          <Element>
            <Text family="GTAR" size="14px" color="#959595">
              Trails B: The Trials B test was the second part, which measured
              mental flexibility. You tapped numbers and letters in alternating,
              sequential order.
            </Text>
          </Element>
          <Flex align="center" marginTop="35px">
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {get(
                  healthReportData,
                  'cognition.mental_flexibility.value'
                )
                  ? get(
                      healthReportData,
                      'cognition.mental_flexibility.value'
                    )
                  : '*'}
                  /200
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Standard Score
              </Text>
            </Element>
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {isNaN(
                  get(
                    healthReportData,
                    'cognition.mental_flexibility.percentile'
                  )
                )
                  ? get(
                      healthReportData,
                      'cognition.mental_flexibility.percentile'
                    ).replace(/[^\d.-]/g, '')
                  : get(
                      healthReportData,
                      'cognition.mental_flexibility.percentile'
                    )}
                %
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Percentile
              </Text>
            </Element>
            <div
              id={get(
                healthReportData,
                'cognition.mental_flexibility.test_name'
              ).replace(/ /g, '_')}
              style={{ width: '25.6%' }}
            ></div>
            <Element>
              <Flex marginLeft="90px">
                <Text family="GTAR" size="16px" color="#222222">
                  Impression:
                  <strong>
                    {' '}
                    {startCase(
                      get(
                        healthReportData,
                        'cognition.mental_flexibility.interpretation'
                      )
                    )}
                  </strong>
                </Text>
              </Flex>
              {/* <Element>
            <Text family="GTAR" size="16px" color="#959595">
              *Timed Out, Only 2 out of 24 tasks are completed
            </Text>
          </Element> */}
            </Element>
          </Flex>
          <Element>
            <Text family="GTAM" size="21px" color="#222222" marginTop="30px">
              Executive Function
            </Text>
            <Text
              family="GTAM"
              size="14px"
              color="#222222"
              borderBottom="2px solid #959595"
              paddingBottom="10px"
            >
              Executive function is a set of mental skills that include working
              memory, flexible thinking, and self-control. We use these skills
              every day to learn, work, and manage daily life, like following
              instruction, focus, handling emotions, and other things.
            </Text>
          </Element>
          <Element>
            <Text family="GTAR" size="14px" color="#959595">
              Digit Symbol Substitution: The Digit Symbol Substitution test has
              been described as a double matching game. You were shown a symbol
              and asked to match that symbol to an adjacent number, then asked
              to press that number.
            </Text>
          </Element>
          <Flex align="center" marginTop="35px">
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {get(
                  healthReportData,
                  'cognition.executive_function.digit_symbol_substitution.value'
                )
                  ? get(
                      healthReportData,
                      'cognition.executive_function.digit_symbol_substitution.value'
                    )
                  : '*'}
                /200
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Standard Score
              </Text>
            </Element>
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {isNaN(
                  get(
                    healthReportData,
                    'cognition.executive_function.digit_symbol_substitution.percentile'
                  )
                )
                  ? get(
                      healthReportData,
                      'cognition.executive_function.digit_symbol_substitution.percentile'
                    ).replace(/[^\d.-]/g, '')
                  : get(
                      healthReportData,
                      'cognition.executive_function.digit_symbol_substitution.percentile'
                    )}
                %
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Percentile
              </Text>
            </Element>
            <div
              id={get(
                healthReportData,
                'cognition.executive_function.digit_symbol_substitution.test_name'
              ).replace(/ /g, '_')}
              style={{ width: '25.6%' }}
            ></div>
            <Flex marginLeft="90px">
              <Text family="GTAR" size="16px" color="#222222">
                Impression:
                <strong>
                  {' '}
                  {startCase(
                    get(
                      healthReportData,
                      'cognition.executive_function.digit_symbol_substitution.interpretation'
                    )
                  )}
                </strong>
              </Text>
            </Flex>
          </Flex>
          <Element marginTop="40px">
            <Text family="GTAR" size="14px" color="#959595">
              Stroop: The Stroop test measures your ability to inhibit reactions
              and control impulses. You were asked to find a word out of a
              larger set of words regardless of the colors, forcing your brain
              to filter out competing signals.
            </Text>
          </Element>
          <Flex align="center" marginTop="35px">
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {get(
                  healthReportData,
                  'cognition.executive_function.stroop.value'
                )
                  ? get(
                      healthReportData,
                      'cognition.executive_function.stroop.value'
                    )
                  : '*'}
                /200
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Standard Score
              </Text>
            </Element>
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {isNaN(
                  get(
                    healthReportData,
                    'cognition.executive_function.stroop.percentile'
                  )
                )
                  ? get(
                      healthReportData,
                      'cognition.executive_function.stroop.percentile'
                    ).replace(/[^\d.-]/g, '')
                  : get(
                      healthReportData,
                      'cognition.executive_function.stroop.percentile'
                    )}
                %
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Percentile
              </Text>
            </Element>
            <div
              id={get(
                healthReportData,
                'cognition.executive_function.stroop.test_name'
              ).replace(/ /g, '_')}
              style={{ width: '25.6%' }}
            ></div>
            <Flex marginLeft="90px">
              <Text family="GTAR" size="16px" color="#222222">
                Impression:
                <strong>
                  {' '}
                  {startCase(
                    get(
                      healthReportData,
                      'cognition.executive_function.stroop.interpretation'
                    )
                  )}
                </strong>
              </Text>
            </Flex>
          </Flex>
          <Element>
            <Text
              family="GTAM"
              size="21px"
              color="#222222"
              borderBottom="2px solid #959595"
              paddingBottom="10px"
            >
              Memory
            </Text>
          </Element>
          <Element>
            <Text family="GTAR" size="14px" color="#959595">
              Immediate Recognition: The Immediate Recognition test was a
              short-term memory test. You were asked to remember ten words, then
              identify those words from a larger pool of words. Short term
              memory represents the acquisition, storage, and recall of new
              information and is used every day in almost all life tasks.
            </Text>
          </Element>
          <Flex align="center" marginTop="35px">
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {get(
                  healthReportData,
                  'cognition.memory.immediate_recognition.value'
                )
                  ? get(
                      healthReportData,
                      'cognition.memory.immediate_recognition.value'
                    )
                  : '*'}
                /200
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Standard Score
              </Text>
            </Element>
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {isNaN(
                  get(
                    healthReportData,
                    'cognition.memory.immediate_recognition.percentile'
                  )
                )
                  ? get(
                      healthReportData,
                      'cognition.memory.immediate_recognition.percentile'
                    ).replace(/[^\d.-]/g, '')
                  : get(
                      healthReportData,
                      'cognition.memory.immediate_recognition.percentile'
                    )}
                %
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Percentile
              </Text>
            </Element>
            <div
              id={get(
                healthReportData,
                'cognition.memory.immediate_recognition.test_name'
              ).replace(/ /g, '_')}
              style={{ width: '25.6%' }}
            ></div>
            <Flex marginLeft="90px">
              <Text family="GTAR" size="16px" color="#222222">
                Impression:
                <strong>
                  {' '}
                  {startCase(
                    get(
                      healthReportData,
                      'cognition.memory.immediate_recognition.interpretation'
                    )
                  )}{' '}
                </strong>
              </Text>
            </Flex>
          </Flex>
          <Element marginTop="40px">
            <Text family="GTAR" size="14px" color="#959595">
              Delayed Recognition: The Delayed Recognition test was a long-term
              memory test. You were asked to identify words from a list that you
              were shown at the beginning of the testing period. Long-term
              memory allows you to build a foundation of information and
              retrieve the information later, either consciously or
              unconsciously.
            </Text>
          </Element>
          <Flex align="center" marginTop="35px">
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {get(
                  healthReportData,
                  'cognition.memory.delayed_recognition.value'
                )
                  ? get(
                      healthReportData,
                      'cognition.memory.delayed_recognition.value'
                    )
                  : '*'}
                /200
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Standard Score
              </Text>
            </Element>
            <Element width="15%">
              <Text family="GTAM" size="24px" color="#222222">
                {isNaN(
                  get(
                    healthReportData,
                    'cognition.memory.delayed_recognition.percentile'
                  )
                )
                  ? get(
                      healthReportData,
                      'cognition.memory.delayed_recognition.percentile'
                    ).replace(/[^\d.-]/g, '')
                  : get(
                      healthReportData,
                      'cognition.memory.delayed_recognition.percentile'
                    )}
                %
              </Text>
              <Text family="GTAR" size="14px" color="#959595">
                Percentile
              </Text>
            </Element>
            <div
              id={get(
                healthReportData,
                'cognition.memory.delayed_recognition.test_name'
              ).replace(/ /g, '_')}
              style={{ width: '25.6%' }}
            ></div>
            <Flex marginLeft="90px">
              <Text family="GTAR" size="16px" color="#222222">
                Impression:
                <strong>
                  {' '}
                  {startCase(
                    get(
                      healthReportData,
                      'cognition.memory.delayed_recognition.interpretation'
                    )
                  )}{' '}
                </strong>
              </Text>
            </Flex>
          </Flex>
        </Element>
      )}
    </Element>
  );
};

export default Cognition;
